import React, { useState, useEffect } from 'react';
import apiService from '@/services/api';
import './AdminAirbnbPricingRules.css';

interface Service {
  id: string;
  name: string;
  description: string;
  display_name: string;
}

interface DecisionNode {
  node_number: number;
  node_label: string;
  total_options: number;
}

interface NodeOption {
  id: number;
  option_key: string;
  option_label: string;
  display_order: number;
}

interface PricingTrigger {
  id: number;
  work_type_multiplier: number;
  structural_multiplier: number;
  material_multiplier: number;
  finish_multiplier: number;
  height_multiplier: number;
  access_multiplier: number;
  complexity_multiplier: number;
  priority_multiplier: number;
  risk_multiplier: number;
  add_cost_gbp: number;
  add_cost_min_gbp: number;
  add_cost_max_gbp: number;
  estimate_type_override: string;
  assessment_flag: boolean;
  confidence_adjustment: number;
}

interface NodeData {
  options: NodeOption[];
  triggers: Map<string, PricingTrigger>;
}

interface BaseVariables {
  id: number;
  service_id: string;
  day_rate_standard_gbp: number;
  day_rate_specialist_gbp: number;
  output_rate_standard: number;
  plant_rate_gbp: number;
  minimum_charge_gbp: number;
  estimate_type_default: string;
  confidence_score_initial: number;
}

interface EditingItem {
  nodeNumber: number;
  rowKey: string;
  field: string;
  value: string;
}

interface Question {
  question_id: string;
  service_type_id: string;
  question_label: string;
  question_type: string;
  options: any;
  is_required: boolean;
  display_order: number;
}

interface AdminPricingRulesCoreConstructionProps {
  serviceType?: string;
}

const MULTIPLIER_FIELDS: { field: keyof PricingTrigger; label: string; icon: string }[] = [
  { field: 'work_type_multiplier',   label: 'Work Type',   icon: '🔨' },
  { field: 'structural_multiplier',  label: 'Structural',  icon: '🏗️' },
  { field: 'material_multiplier',    label: 'Material',    icon: '🧱' },
  { field: 'finish_multiplier',      label: 'Finish',      icon: '✨' },
  { field: 'height_multiplier',      label: 'Height',      icon: '📏' },
  { field: 'access_multiplier',      label: 'Access',      icon: '🚪' },
  { field: 'complexity_multiplier',  label: 'Complexity',  icon: '⚙️' },
  { field: 'priority_multiplier',    label: 'Priority',    icon: '⚡' },
  { field: 'risk_multiplier',        label: 'Risk',        icon: '⚠️' },
];

export default function AdminPricingRulesCoreConstruction({ serviceType }: AdminPricingRulesCoreConstructionProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<string>(serviceType || '');
  const [nodes, setNodes] = useState<DecisionNode[]>([]);
  const [allNodesData, setAllNodesData] = useState<Map<number, NodeData>>(new Map());
  const [baseVariables, setBaseVariables] = useState<BaseVariables | null>(null);
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionEdits, setQuestionEdits] = useState<Record<string, any>>({});

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    if (selectedService) {
      loadEverything(selectedService);
      loadQuestions(selectedService);
    }
  }, [selectedService]);

  const loadQuestions = async (serviceId: string) => {
    try {
      const res = await apiService.request(`/admin/pricing-extended/questions/${serviceId}`, 'GET');
      if (res.success) {
        const qs: Question[] = res.questions || [];
        setQuestions(qs);
        const edits: Record<string, any> = {};
        qs.forEach((q) => {
          edits[q.question_id] = {
            label: q.question_label,
            options: q.options ? JSON.stringify(q.options, null, 2) : '',
            isRequired: q.is_required,
          };
        });
        setQuestionEdits(edits);
      }
    } catch {
      // silently ignore
    }
  };

  const saveQuestion = async (questionId: string) => {
    const edit = questionEdits[questionId];
    if (!edit) return;
    try {
      let parsedOptions = null;
      if (edit.options && edit.options.trim()) {
        parsedOptions = JSON.parse(edit.options);
      }
      await apiService.request(`/admin/pricing-extended/questions/${questionId}`, 'PUT', {
        label: edit.label,
        options: parsedOptions,
        isRequired: edit.isRequired,
      });
      setSuccess('Question saved!');
      setTimeout(() => setSuccess(null), 3000);
      await loadQuestions(selectedService);
    } catch {
      setError('Failed to save question');
    }
  };

  const loadServices = async () => {
    try {
      setLoading(true);
      const res = await apiService.request('/admin/pricing/core-construction/services', 'GET');
      if (res.success) {
        const all: Service[] = res.data || [];
        setServices(all);
        if (serviceType) {
          const match = all.find(s => s.id === serviceType);
          if (match) setSelectedService(match.id);
        } else if (all.length > 0) {
          setSelectedService(all[0].id);
        }
      }
    } catch {
      setError('Failed to load services');
      setLoading(false);
    }
  };

  const loadEverything = async (serviceId: string) => {
    try {
      setLoading(true);
      setAllNodesData(new Map());

      // Load nodes and base variables in parallel
      const [nodesRes, baseRes] = await Promise.all([
        apiService.request(`/admin/pricing/core-construction/${serviceId}/nodes`, 'GET'),
        apiService.request(`/admin/pricing/core-construction/${serviceId}/base-variables`, 'GET'),
      ]);

      const fetchedNodes: DecisionNode[] = nodesRes.success ? (nodesRes.data || []) : [];
      setNodes(fetchedNodes);
      if (baseRes.success) setBaseVariables(baseRes.data);

      // Load all nodes' options + triggers
      const dataMap = new Map<number, NodeData>();

      await Promise.all(
        fetchedNodes.map(async (node) => {
          const optsRes = await apiService.request(
            `/admin/pricing/core-construction/${serviceId}/nodes/${node.node_number}/options`,
            'GET'
          );
          const opts: NodeOption[] = optsRes.success ? (optsRes.data || []) : [];

          const triggersMap = new Map<string, PricingTrigger>();
          await Promise.all(
            opts.map(async (opt) => {
              try {
                const tRes = await apiService.request(
                  `/admin/pricing/core-construction/${serviceId}/nodes/${node.node_number}/options/${opt.option_key}/triggers`,
                  'GET'
                );
                if (tRes.success && tRes.data) {
                  const t = tRes.data;
                  triggersMap.set(opt.option_key, {
                    ...t,
                    work_type_multiplier:  Number(t.work_type_multiplier)  || 1,
                    structural_multiplier: Number(t.structural_multiplier) || 1,
                    material_multiplier:   Number(t.material_multiplier)   || 1,
                    finish_multiplier:     Number(t.finish_multiplier)     || 1,
                    height_multiplier:     Number(t.height_multiplier)     || 1,
                    access_multiplier:     Number(t.access_multiplier)     || 1,
                    complexity_multiplier: Number(t.complexity_multiplier) || 1,
                    priority_multiplier:   Number(t.priority_multiplier)   || 1,
                    risk_multiplier:       Number(t.risk_multiplier)       || 1,
                    add_cost_gbp:          Number(t.add_cost_gbp)          || 0,
                    add_cost_min_gbp:      Number(t.add_cost_min_gbp)      || 0,
                    add_cost_max_gbp:      Number(t.add_cost_max_gbp)      || 0,
                    confidence_adjustment: Number(t.confidence_adjustment) || 0,
                  });
                }
              } catch {
                // skip
              }
            })
          );

          dataMap.set(node.node_number, { options: opts, triggers: triggersMap });
        })
      );

      setAllNodesData(dataMap);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const saveTriggerField = async (nodeNumber: number, optionKey: string, field: string, rawValue: string) => {
    const trigger = allNodesData.get(nodeNumber)?.triggers.get(optionKey);
    if (!trigger) return;

    const isNumericField = field !== 'estimate_type_override';
    const parsedValue = isNumericField ? parseFloat(rawValue) : rawValue;

    try {
      await apiService.request(
        `/admin/pricing/core-construction/triggers/${trigger.id}`,
        'PUT',
        { [field]: parsedValue }
      );
      setSuccess('Updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
      setEditingItem(null);
      await loadEverything(selectedService);
    } catch {
      setError('Failed to update value');
    }
  };

  // ── Render helpers ──────────────────────────────────────────────

  const renderMultiplierCard = (
    nodeNumber: number,
    nodeLabel: string,
    options: NodeOption[],
    triggers: Map<string, PricingTrigger>,
    field: keyof PricingTrigger,
    label: string,
    icon: string
  ) => (
    <div className="rule-card" key={`${nodeNumber}-${field}`}>
      <h3>{icon} {label} — {nodeLabel}</h3>
      <table>
        <thead>
          <tr>
            <th>Option</th>
            <th>Multiplier</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {options.map((opt) => {
            const trigger = triggers.get(opt.option_key);
            if (!trigger) return null;
            const value = trigger[field] as number;
            const isEditing =
              editingItem?.nodeNumber === nodeNumber &&
              editingItem?.rowKey === opt.option_key &&
              editingItem?.field === field;
            return (
              <tr key={opt.option_key}>
                <td><code>{opt.option_label}</code></td>
                <td className="value">
                  {isEditing
                    ? <input
                        type="number" step="0.01" min="0.5" max="3"
                        value={editingItem.value}
                        onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })}
                      />
                    : <kbd>×{value.toFixed(2)}</kbd>
                  }
                </td>
                <td>
                  {isEditing
                    ? <>
                        <button className="btn-save" onClick={() => saveTriggerField(nodeNumber, opt.option_key, field, editingItem.value)}>Save</button>
                        <button className="btn-cancel" onClick={() => setEditingItem(null)}>Cancel</button>
                      </>
                    : <button className="btn-edit" onClick={() => setEditingItem({ nodeNumber, rowKey: opt.option_key, field, value: value.toString() })}>Edit</button>
                  }
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderFixedCostCard = (
    nodeNumber: number,
    nodeLabel: string,
    options: NodeOption[],
    triggers: Map<string, PricingTrigger>
  ) => (
    <div className="rule-card" key={`fc-${nodeNumber}`}>
      <h3>💵 Fixed Costs — {nodeLabel}</h3>
      <table>
        <thead>
          <tr>
            <th>Option</th>
            <th>Cost ($)</th>
            <th>Range</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {options.map((opt) => {
            const trigger = triggers.get(opt.option_key);
            if (!trigger) return null;
            const isEditing =
              editingItem?.nodeNumber === nodeNumber &&
              editingItem?.rowKey === opt.option_key &&
              editingItem?.field === 'add_cost_gbp';
            const hasRange = trigger.add_cost_min_gbp || trigger.add_cost_max_gbp;
            return (
              <tr key={opt.option_key}>
                <td><strong>{opt.option_label}</strong></td>
                <td className="value">
                  {isEditing
                    ? <input type="number" step="0.01" value={editingItem.value}
                        onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })} />
                    : `$${trigger.add_cost_gbp.toFixed(2)}`
                  }
                </td>
                <td>{hasRange ? `$${trigger.add_cost_min_gbp}–$${trigger.add_cost_max_gbp}` : '—'}</td>
                <td>
                  {isEditing
                    ? <>
                        <button className="btn-save" onClick={() => saveTriggerField(nodeNumber, opt.option_key, 'add_cost_gbp', editingItem.value)}>Save</button>
                        <button className="btn-cancel" onClick={() => setEditingItem(null)}>Cancel</button>
                      </>
                    : <button className="btn-edit" onClick={() => setEditingItem({ nodeNumber, rowKey: opt.option_key, field: 'add_cost_gbp', value: trigger.add_cost_gbp.toString() })}>Edit</button>
                  }
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderConfigCard = (
    nodeNumber: number,
    nodeLabel: string,
    options: NodeOption[],
    triggers: Map<string, PricingTrigger>
  ) => (
    <div className="rule-card" key={`cfg-${nodeNumber}`}>
      <h3>🔧 Estimate Type &amp; Confidence — {nodeLabel}</h3>
      <table>
        <thead>
          <tr>
            <th>Option</th>
            <th>Estimate Type</th>
            <th>Confidence Adj.</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {options.map((opt) => {
            const trigger = triggers.get(opt.option_key);
            if (!trigger) return null;
            const isEditingType =
              editingItem?.nodeNumber === nodeNumber &&
              editingItem?.rowKey === opt.option_key &&
              editingItem?.field === 'estimate_type_override';
            const isEditingConf =
              editingItem?.nodeNumber === nodeNumber &&
              editingItem?.rowKey === opt.option_key &&
              editingItem?.field === 'confidence_adjustment';
            return (
              <tr key={opt.option_key}>
                <td><strong>{opt.option_label}</strong></td>
                <td className="value">
                  {isEditingType
                    ? <select value={editingItem.value}
                        onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })}
                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3498db', fontSize: '13px' }}>
                        <option value="">None</option>
                        <option value="FIXED">FIXED</option>
                        <option value="RANGE">RANGE</option>
                        <option value="ASSESSMENT">ASSESSMENT</option>
                      </select>
                    : <code>{trigger.estimate_type_override || 'None'}</code>
                  }
                </td>
                <td className="value">
                  {isEditingConf
                    ? <input type="number" step="1" min="-50" max="50"
                        value={editingItem.value}
                        onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })} />
                    : <kbd>{trigger.confidence_adjustment >= 0 ? '+' : ''}{trigger.confidence_adjustment}</kbd>
                  }
                </td>
                <td>
                  {isEditingType || isEditingConf
                    ? <>
                        <button className="btn-save" onClick={() => saveTriggerField(nodeNumber, opt.option_key, editingItem!.field, editingItem!.value)}>Save</button>
                        <button className="btn-cancel" onClick={() => setEditingItem(null)}>Cancel</button>
                      </>
                    : <>
                        <button className="btn-edit" style={{ marginRight: '4px' }}
                          onClick={() => setEditingItem({ nodeNumber, rowKey: opt.option_key, field: 'estimate_type_override', value: trigger.estimate_type_override || '' })}>Type</button>
                        <button className="btn-edit"
                          onClick={() => setEditingItem({ nodeNumber, rowKey: opt.option_key, field: 'confidence_adjustment', value: trigger.confidence_adjustment.toString() })}>Conf</button>
                      </>
                  }
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const serviceName = services.find(s => s.id === selectedService)?.name || selectedService;

  if (loading && services.length === 0) {
    return <div className="loading">Loading pricing rules...</div>;
  }

  return (
    <div className="admin-airbnb-pricing">
      <header className="pagination-header">
        <div>
          <h1>⚙️ Core Construction & Structural Pricing</h1>
          <p>Admin-configurable pricing engine for blockwork, bricklaying, concrete works, and structural services</p>
        </div>
      </header>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* ── SERVICE SELECTOR ── */}
      <div className="service-selector" style={{ margin: '1.5rem 0', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <h3 style={{ marginTop: 0, fontSize: '0.95rem', color: '#6b7280' }}>Select Service Category</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
          {services.map((service) => (
            <button
              key={service.id}
              onClick={() => setSelectedService(service.id)}
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '6px',
                border: selectedService === service.id ? '2px solid #3b82f6' : '1px solid #d1d5db',
                backgroundColor: selectedService === service.id ? '#eff6ff' : '#ffffff',
                color: selectedService === service.id ? '#1e40af' : '#6b7280',
                fontWeight: selectedService === service.id ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '0.9rem'
              }}
            >
              {service.name}
            </button>
          ))}
        </div>
      </div>

      <div className="tabs">
        <button className={activeTab === 'overview'    ? 'active' : ''} onClick={() => setActiveTab('overview')}>Overview</button>
        <button className={activeTab === 'base'        ? 'active' : ''} onClick={() => setActiveTab('base')}>Base Variables</button>
        <button className={activeTab === 'multipliers' ? 'active' : ''} onClick={() => setActiveTab('multipliers')}>Multipliers</button>
        <button className={activeTab === 'costs'       ? 'active' : ''} onClick={() => setActiveTab('costs')}>Fixed Costs & Config</button>
        <button className={activeTab === 'formulas'    ? 'active' : ''} onClick={() => setActiveTab('formulas')}>Formulas</button>
        <button className={activeTab === 'questions'   ? 'active' : ''} onClick={() => setActiveTab('questions')}>Questions ({questions.length})</button>
      </div>

      <div className="content">

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div className="overview">
            <div className="info-card">
              <h3>Pricing Formula</h3>
              <pre>{`Final = (DayRate / OutputRate) × Volume
      × WorkTypeMultiplier
      × StructuralMultiplier
      × MaterialMultiplier
      × FinishMultiplier
      × HeightMultiplier
      × AccessMultiplier
      × ComplexityMultiplier
      × PriorityMultiplier
      × RiskMultiplier
      + PlantCosts + FixedCosts
      (minimum = MinimumCharge)`}</pre>
            </div>

            <div className="info-grid">
              <div className="stat">
                <h4>Day Rate (Standard)</h4>
                <p className="value">{baseVariables ? `$${baseVariables.day_rate_standard_gbp}` : '—'}</p>
              </div>
              <div className="stat">
                <h4>Output Rate</h4>
                <p className="value">{baseVariables ? `${baseVariables.output_rate_standard} units/day` : '—'}</p>
              </div>
              <div className="stat">
                <h4>Plant Rate</h4>
                <p className="value">{baseVariables ? `$${baseVariables.plant_rate_gbp}/day` : '—'}</p>
              </div>
              <div className="stat">
                <h4>Minimum Charge</h4>
                <p className="value">{baseVariables ? `$${baseVariables.minimum_charge_gbp}` : '—'}</p>
              </div>
            </div>

            <div className="info-card">
              <h3>Decision Nodes</h3>
              <div className="info-grid">
                {nodes.map((node) => (
                  <div className="stat" key={node.node_number}>
                    <h4>{node.node_label}</h4>
                    <p className="value">{node.total_options}</p>
                    <p style={{ color: '#555', margin: 0, fontSize: '13px' }}>options</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="info-card">
              <h3>📊 Example Calculation</h3>
              <p><strong>Scenario:</strong> Standard blockwork, cavity wall construction, medium height (3–6 m), open access, 25 m² volume</p>

              <div className="multipliers-grid">
                <div className="multiplier-item">
                  <span className="label">Work Type</span>
                  <span className="value">×1.00</span>
                  <span className="description">Standard</span>
                </div>
                <div className="multiplier-item">
                  <span className="label">Structural</span>
                  <span className="value">×1.15</span>
                  <span className="description">Cavity wall</span>
                </div>
                <div className="multiplier-item">
                  <span className="label">Material</span>
                  <span className="value">×1.05</span>
                  <span className="description">Standard block</span>
                </div>
                <div className="multiplier-item">
                  <span className="label">Height</span>
                  <span className="value">×1.10</span>
                  <span className="description">3–6 m</span>
                </div>
                <div className="multiplier-item">
                  <span className="label">Access</span>
                  <span className="value">×1.00</span>
                  <span className="description">Open site</span>
                </div>
                <div className="multiplier-item">
                  <span className="label">Complexity</span>
                  <span className="value">×1.00</span>
                  <span className="description">Straightforward</span>
                </div>
              </div>

              <p className="calculation-note">
                <strong>Combined Multiplier:</strong> 1.00 × 1.15 × 1.05 × 1.10 × 1.00 × 1.00 = <strong>1.328×</strong>
              </p>

              <table className="calculation-table">
                <tbody>
                  <tr>
                    <td className="label">Base Labour (25 m² × £28/m²)</td>
                    <td className="value">£700.00</td>
                  </tr>
                  <tr>
                    <td className="label">After Multipliers (1.328×)</td>
                    <td className="value">£929.60</td>
                  </tr>
                  <tr>
                    <td className="label">+ Plant (scaffold, mixer)</td>
                    <td className="value">+£120.00</td>
                  </tr>
                  <tr className="divider">
                    <td className="label"><strong>Subtotal</strong></td>
                    <td className="value"><strong>£1,049.60</strong></td>
                  </tr>
                  <tr>
                    <td className="label">VAT (20%)</td>
                    <td className="value">+£209.92</td>
                  </tr>
                  <tr className="total">
                    <td className="label"><strong>GRAND TOTAL</strong></td>
                    <td className="value"><strong>£1,259.52</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── BASE VARIABLES ── */}
        {activeTab === 'base' && (
          <div className="sections">
            <div className="rule-card">
              <h3>� Base Rate Configuration</h3>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                Core pricing variables used across all nodes for <strong>{serviceName}</strong>. Edit these to adjust base pricing.
              </p>
              {baseVariables ? (
                <table>
                  <thead>
                    <tr>
                      <th>Variable</th>
                      <th>Current Value</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Day Rate (Standard)</strong><br /><span style={{ fontSize: '0.8rem', color: '#999' }}>per day</span></td>
                      <td className="value">£{baseVariables.day_rate_standard_gbp}</td>
                      <td><button className="btn-edit" style={{ fontSize: '0.85rem' }}>Edit</button></td>
                    </tr>
                    <tr>
                      <td><strong>Day Rate (Specialist)</strong><br /><span style={{ fontSize: '0.8rem', color: '#999' }}>per day</span></td>
                      <td className="value">£{baseVariables.day_rate_specialist_gbp}</td>
                      <td><button className="btn-edit" style={{ fontSize: '0.85rem' }}>Edit</button></td>
                    </tr>
                    <tr>
                      <td><strong>Output Rate</strong><br /><span style={{ fontSize: '0.8rem', color: '#999' }}>units/day (m², lm, etc)</span></td>
                      <td className="value">{baseVariables.output_rate_standard}</td>
                      <td><button className="btn-edit" style={{ fontSize: '0.85rem' }}>Edit</button></td>
                    </tr>
                    <tr>
                      <td><strong>Plant Rate</strong><br /><span style={{ fontSize: '0.8rem', color: '#999' }}>per day</span></td>
                      <td className="value">£{baseVariables.plant_rate_gbp}</td>
                      <td><button className="btn-edit" style={{ fontSize: '0.85rem' }}>Edit</button></td>
                    </tr>
                    <tr>
                      <td><strong>Minimum Charge</strong><br /><span style={{ fontSize: '0.8rem', color: '#999' }}>base fee</span></td>
                      <td className="value">£{baseVariables.minimum_charge_gbp}</td>
                      <td><button className="btn-edit" style={{ fontSize: '0.85rem' }}>Edit</button></td>
                    </tr>
                    <tr>
                      <td><strong>Default Estimate Type</strong><br /><span style={{ fontSize: '0.8rem', color: '#999' }}>FIXED/RANGE/ASSESS</span></td>
                      <td><code>{baseVariables.estimate_type_default}</code></td>
                      <td><button className="btn-edit" style={{ fontSize: '0.85rem' }}>Edit</button></td>
                    </tr>
                    <tr>
                      <td><strong>Initial Confidence Score</strong><br /><span style={{ fontSize: '0.8rem', color: '#999' }}>0-100</span></td>
                      <td><kbd>{baseVariables.confidence_score_initial}</kbd></td>
                      <td><button className="btn-edit" style={{ fontSize: '0.85rem' }}>Edit</button></td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p style={{ color: '#7f8c8d' }}>No base variables available for this service.</p>
              )}
            </div>
          </div>
        )}

        {/* ── MULTIPLIERS ── */}
        {activeTab === 'multipliers' && (
          <div className="sections">
            {loading && <p style={{ color: '#7f8c8d' }}>Loading...</p>}
            {!loading && nodes.map((node) => {
              const nodeData = allNodesData.get(node.node_number);
              if (!nodeData || nodeData.options.length === 0) return null;
              return MULTIPLIER_FIELDS.map(({ field, label, icon }) =>
                renderMultiplierCard(node.node_number, node.node_label, nodeData.options, nodeData.triggers, field, label, icon)
              );
            })}
          </div>
        )}

        {/* ── FIXED COSTS ── */}
        {activeTab === 'costs' && (
          <div className="sections">
            {loading && <p style={{ color: '#7f8c8d' }}>Loading...</p>}
            {!loading && nodes.map((node) => {
              const nodeData = allNodesData.get(node.node_number);
              if (!nodeData || nodeData.options.length === 0) return null;
              return (
                <React.Fragment key={node.node_number}>
                  {renderFixedCostCard(node.node_number, node.node_label, nodeData.options, nodeData.triggers)}
                  {renderConfigCard(node.node_number, node.node_label, nodeData.options, nodeData.triggers)}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* ── FORMULAS ── */}
        {activeTab === 'formulas' && (
          <div className="sections">
            <div className="rule-card">
              <h3>📐 Pricing Calculation Formula</h3>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                The core formula used to calculate pricing for {serviceName}. All multipliers are applied in sequence.
              </p>
              <pre style={{ backgroundColor: '#f3f4f6', padding: '1rem', borderRadius: '6px', fontSize: '0.85rem', overflow: 'auto' }}>{`Final Quote =
  (Labour Rate × Volume ÷ Output Rate)
  × WorkTypeMultiplier
  × StructuralMultiplier
  × MaterialMultiplier
  × FinishMultiplier
  × HeightMultiplier
  × AccessMultiplier
  × ComplexityMultiplier
  × RiskMultiplier
  × PriorityMultiplier
  + PlantCosts
  + FixedAddCosts
  (minimum: MinimumCharge)

Estimate Type Decision:
  IF conditions_met_for_fixed THEN 'FIXED' (no range shown)
  ELSE IF conditions_met_for_range THEN 'RANGE' (show min-max)
  ELSE 'ASSESSMENT' (requires site visit)`}</pre>
            </div>

            <div className="rule-card">
              <h3>🔢 Base Variables Reference</h3>
              {baseVariables && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #d1d5db', fontWeight: 600 }}>Variable</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #d1d5db', fontWeight: 600 }}>Value</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #d1d5db', fontWeight: 600 }}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem' }}><code>DayRate_Standard</code></td>
                      <td style={{ padding: '0.75rem' }}>£{baseVariables.day_rate_standard_gbp}</td>
                      <td style={{ padding: '0.75rem' }}>Standard labour day rate</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem' }}><code>DayRate_Specialist</code></td>
                      <td style={{ padding: '0.75rem' }}>£{baseVariables.day_rate_specialist_gbp}</td>
                      <td style={{ padding: '0.75rem' }}>Specialist labour day rate</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem' }}><code>OutputRate</code></td>
                      <td style={{ padding: '0.75rem' }}>{baseVariables.output_rate_standard} units/day</td>
                      <td style={{ padding: '0.75rem' }}>Standard productivity (m², sq.m, etc)</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem' }}><code>PlantRate</code></td>
                      <td style={{ padding: '0.75rem' }}>£{baseVariables.plant_rate_gbp}/day</td>
                      <td style={{ padding: '0.75rem' }}>Plant hire or equipment cost</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem' }}><code>MinimumCharge</code></td>
                      <td style={{ padding: '0.75rem' }}>£{baseVariables.minimum_charge_gbp}</td>
                      <td style={{ padding: '0.75rem' }}>Minimum quote regardless of calculation</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.75rem' }}><code>EstimateType_Default</code></td>
                      <td style={{ padding: '0.75rem' }}>{baseVariables.estimate_type_default}</td>
                      <td style={{ padding: '0.75rem' }}>FIXED, RANGE, or ASSESSMENT</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            <div className="rule-card">
              <h3>📊 Multiplier Stacking Example</h3>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                This example shows how multipliers combine for a typical {serviceName} project:
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #d1d5db', fontWeight: 600 }}>Multiplier Type</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #d1d5db', fontWeight: 600 }}>Value</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #d1d5db', fontWeight: 600 }}>Example Scenario</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}><strong>Work Type</strong></td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>×1.20</td>
                    <td style={{ padding: '0.75rem' }}>New construction (vs repair)</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}><strong>Material</strong></td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>×1.10</td>
                    <td style={{ padding: '0.75rem' }}>Premium materials</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}><strong>Height/Access</strong></td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>×1.15</td>
                    <td style={{ padding: '0.75rem' }}>Elevated work, restricted access</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}><strong>Complexity</strong></td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>×1.05</td>
                    <td style={{ padding: '0.75rem' }}>Moderate complexity</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem' }}><strong>Combined Total</strong></td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600 }}>×1.77</td>
                    <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#059669' }}>1.20 × 1.10 × 1.15 × 1.05 = 1.77</td>
                  </tr>
                </tbody>
              </table>
              <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                <strong>Result:</strong> Base price of £1000 → £1000 × 1.77 = <strong>£1,770 estimated cost</strong>
              </p>
            </div>

            <div className="rule-card">
              <h3>✅ Estimate Type Rules</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                <div style={{ padding: '1rem', backgroundColor: '#dcfce7', borderRadius: '6px', border: '1px solid #86efac' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#166534' }}>FIXED Estimate</h4>
                  <p style={{ margin: '0.5rem 0', fontSize: '0.85rem', color: '#166534' }}>✓ Highly confident conditions only</p>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#166534' }}>✓ All variables known and confirmed</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fcd34d' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#92400e' }}>RANGE Estimate</h4>
                  <p style={{ margin: '0.5rem 0', fontSize: '0.85rem', color: '#92400e' }}>⚠ Some uncertainty present</p>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#92400e' }}>⚠ Shows min-max pricing</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: '#fee2e2', borderRadius: '6px', border: '1px solid #fca5a5' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#7f1d1d' }}>ASSESSMENT Required</h4>
                  <p style={{ margin: '0.5rem 0', fontSize: '0.85rem', color: '#7f1d1d' }}>✗ High-risk work</p>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#7f1d1d' }}>✗ Site visit mandatory</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── QUESTIONS ── */}
        {activeTab === 'questions' && (
          <div className="sections">
            <div className="rule-card">
              <h3>Customer Form Questions</h3>
              <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                Questions shown to customers when requesting a quote for {serviceName}. Edit labels, options (JSON), and required status.
              </p>
              {questions.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                  <p>No questions configured yet.</p>
                  <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                    Run <code>sql/seed-core-construction-questions.sql</code> to populate questions.
                  </p>
                </div>
              ) : (
                <div>
                  {questions.map((q) => {
                    const edit = questionEdits[q.question_id] || { label: q.question_label, options: '', isRequired: q.is_required };
                    return (
                      <div key={q.question_id} style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        padding: '1rem',
                        marginBottom: '1rem',
                        backgroundColor: '#fafafa'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem', fontFamily: 'monospace' }}>
                              {q.question_id}{' · '}type: <strong>{q.question_type}</strong>{' · '}order: {q.display_order}
                            </div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>Label</label>
                            <input
                              type="text"
                              value={edit.label}
                              onChange={(e) => setQuestionEdits(prev => ({ ...prev, [q.question_id]: { ...edit, label: e.target.value } }))}
                              style={{ width: '100%', marginBottom: '0.5rem', padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem' }}
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem' }}>
                            <input
                              type="checkbox"
                              id={`req_${q.question_id}`}
                              checked={edit.isRequired}
                              onChange={(e) => setQuestionEdits(prev => ({ ...prev, [q.question_id]: { ...edit, isRequired: e.target.checked } }))}
                            />
                            <label htmlFor={`req_${q.question_id}`} style={{ cursor: 'pointer', fontSize: '0.875rem' }}>Required</label>
                          </div>
                        </div>
                        {q.question_type !== 'text' && q.question_type !== 'textarea' && q.question_type !== 'number' && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>Options (JSON array)</label>
                            <textarea
                              value={edit.options}
                              onChange={(e) => setQuestionEdits(prev => ({ ...prev, [q.question_id]: { ...edit, options: e.target.value } }))}
                              rows={4}
                              style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8rem', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', resize: 'vertical' }}
                            />
                          </div>
                        )}
                        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                          <button className="btn-save" onClick={() => saveQuestion(q.question_id)}>Save</button>
                          <button className="btn-cancel" onClick={() => setQuestionEdits(prev => ({
                            ...prev,
                            [q.question_id]: { label: q.question_label, options: q.options ? JSON.stringify(q.options, null, 2) : '', isRequired: q.is_required }
                          }))}>Reset</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
